import React, { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { consistentString } from '../../utils/content-formatting';
import useWindowSize from '../../utils/window-size';
import {
  d3,
  parseNumbers,
  formatDatasets,
  numberFormats,
  lineColorsMap,
  GetElementWidth,
} from './functions';

const Graph = ({
  title,
  xAxisLabel,
  yAxisLabel,
  id,
  yAxisStart,
  yAxisFormat,
  csvData,
  lineColors,
  description,
}) => {
  const ref = useRef(null);

  const { isMobile } = useWindowSize();

  const drawChart = useCallback(() => {
    const width = ref.current.offsetWidth;
    const height = ref.current.offsetWidth / 2;
    const margin = {
      left: 125,
      right: 50,
      top: 16,
      bottom: 65,
    };

    const svg = d3
      .select(`#graph-container-${id}`)
      .append('svg')
      .attr('class', 'graph')
      .attr('role', 'img')
      .attr('aria-described-by', `graph-title-${id}`)
      .attr('height', height)
      .attr('width', width)
      .attr('preserveAspectRatio', 'xMinYMin meet')
      .attr(
        'viewBox',
        `${-margin.left} ${-margin.top} ${width + margin.left + margin.right} ${
          height + margin.top + margin.bottom
        } `
      );

    svg.append('title').attr('id', `graph-title-${id}`).text(description);

    d3.csv(csvData).then((dataset) => {
      // Parse and format the data
      parseNumbers(dataset);
      const sets = formatDatasets(dataset);
      // Find min/max values for both x and y axes
      const yAxisMax = d3.max(sets, (c) => d3.max(c.values, (d) => d.value));
      const yAxisMin = d3.min(sets, (c) => d3.min(c.values, (d) => d.value));
      const xAxisMax = d3.max(sets, (c) => d3.max(c.values, (d) => d.key));
      const xAxisMin = d3.min(sets, (c) => d3.min(c.values, (d) => d.key));

      // Set the scales
      const xScale = d3
        .scaleTime()
        .domain([xAxisMin, xAxisMax])
        .range([0, width])
        .nice(); // We may or may not want to call .nice() here - it ensures round start and end ticks are always shown but sometimes this is beyond the bounds of the data set (2020 will show when the set ends in 2019 for example)

      const yScale = d3
        .scaleLinear()
        .domain([yAxisStart === 'Minimum' ? yAxisMin : 0, yAxisMax])
        .nice()
        .rangeRound([height, 0]);

      // Configure y axis
      const yaxis = d3
        .axisLeft()
        .ticks(isMobile ? 5 : 10)
        .tickSize(-width)
        .tickFormat(numberFormats[yAxisFormat] || numberFormats.default)
        .tickPadding(24)
        .scale(yScale);

      // Configure x axis
      // This sets the maximum of ticks on the x-axis to 10 for desktop screens and 5 for mobile
      const numYears =
        Math.floor((xAxisMax - xAxisMin) / (365 * 24 * 60 * 60 * 1000)) + 1;
      const yearInterval = Math.ceil((numYears / 10) * (isMobile ? 2 : 1));

      const xaxis = d3
        .axisBottom()
        .ticks(d3.timeYear.every(yearInterval))
        .tickPadding(24)
        .tickSize(0)
        .tickFormat(d3.timeFormat('%Y'))
        .scale(xScale);

      // Draw the x axis
      svg
        .append('g')
        .attr('class', 'axis')
        .attr('transform', `translate(0,${height})`)
        .style('font-family', 'inherit')
        .call(xaxis);

      // Draw the x axis label (if provided)
      if (xAxisLabel) {
        svg
          .append('text')
          .attr('class', 'axis-label')
          .attr(
            'transform',
            `translate(${width / 2} ,${height + margin.bottom})`
          )
          .style('text-anchor', 'middle')
          .text(xAxisLabel);
      }

      // Draw the y axis
      svg
        .append('g')
        .attr('class', 'axis')
        .style('font-family', 'inherit')
        .call(yaxis);

      // Draw the y axis label (if provided)
      if (yAxisLabel) {
        svg
          .append('text')
          .attr('class', 'axis-label')
          .attr('transform', 'rotate(-90)')
          .attr('y', 0 - margin.left)
          .attr('x', 0 - height / 2)
          .attr('dy', '1em')
          .style('text-anchor', 'middle')
          .text(yAxisLabel);
      }

      // Configure the lines
      const lineConfig = d3
        .line()
        .x((d) => xScale(d.key))
        .y((d) => yScale(d.value));

      // Select and draw the lines
      const lines = svg.selectAll('lines').data(sets).enter().append('g');
      const strokeWidth = 5;
      const dashWidth = 5;

      lines
        .append('path')
        .attr('d', (d) => lineConfig(d.values))
        .attr('fill', 'none')
        .attr('stroke', (d, i) => lineColorsMap[lineColors[i]])
        .attr('stroke-width', strokeWidth)
        .attr('stroke-dasharray', (d, i) => i * dashWidth)
        .attr('class', (d) => `line-${consistentString(d.set)}`);

      // Select the key svg
      const keySvg = d3.select(`#key-container-${id}`);

      if (!document.querySelector(`#key-container-${id} .key`)) {
        // Draw the key

        const legendElement = keySvg
          .append('div')
          .attr('class', 'key')
          .attr('width', width)
          .selectAll('div.key')
          .append('div')
          .data(sets.map((set) => set.set))
          .enter()
          .append('div')
          .attr('class', 'key-list');

        legendElement
          .append('svg')
          .attr('height', 46)
          .attr('width', 46)
          .append('line')
          .attr('stroke', (d, i) => lineColorsMap[lineColors[i]])
          .attr('stroke-dasharray', (d, i) => i * dashWidth)
          .style('stroke-width', 5)
          .attr('x1', 0)
          .attr('y1', 23)
          .attr('x2', 46)
          .attr('y2', 23);

        legendElement.append('text').text((d) => d);
      }
    });
  }, [
    csvData,
    id,
    lineColors,
    xAxisLabel,
    yAxisFormat,
    yAxisLabel,
    yAxisStart,
    isMobile,
    title,
  ]);

  const size = GetElementWidth(ref);

  useEffect(() => {
    // remove keys and graphs before re-render
    const graphSVG = d3.selectAll(`#graph-container-${id} .graph`);
    const keySVG = d3.selectAll(`#key-container-${id} .key`);

    if (graphSVG) {
      graphSVG.remove();
    }

    if (keySVG) {
      keySVG.remove();
    }

    if (csvData && size > 0) {
      drawChart();
    }
  }, [csvData, drawChart, id, size]);

  return (
    <div ref={ref}>
      <h3>{title}</h3>
      <div id={`graph-container-${id}`} />
      <div id={`key-container-${id}`} aria-hidden="true">
        <h3>Key</h3>
      </div>
    </div>
  );
};

Graph.propTypes = {
  title: PropTypes.string,
  xAxisLabel: PropTypes.string,
  yAxisLabel: PropTypes.string,
  id: PropTypes.string,
  yAxisStart: PropTypes.string,
  yAxisFormat: PropTypes.string,
  csvData: PropTypes.string,
  lineColors: PropTypes.array,
  description: PropTypes.string,
};

export default Graph;
